import { toQueryString, joinPaths, accessNested } from 'utils/utils';
import Store from 'store/store';

export async function api(endpoint, options = {}, fetchOptions = {}) {
	if (typeof window === 'undefined') {
		throw new Error('Trying to request server-side');
	}

	fetchOptions.headers = fetchOptions.headers || {};
	if (!fetchOptions.headers['content-type']) {
		fetchOptions.headers['content-type'] = 'application/json';
	}

	fetchOptions.referrer = '';

	// Move lat/lon to headers
	if (options.lon && options.lat) {
		fetchOptions.headers['x-requested-lat'] = options.lat;
		fetchOptions.headers['x-requested-lon'] = options.lon;
		options = { ...options };
		delete options.lat;
		delete options.lon;
	}
	// Move bounding regions to headers
	if (options.countries) {
		fetchOptions.headers['x-requested-countries'] = options.countries;
		options = { ...options };
		delete options.countries;
	}
	// Move location approximation (used for caching - typically city name) to headers
	if (options.approxPos) {
		fetchOptions.headers['x-requested-approxpos'] = options.approxPos.replace(/[^ -~]/g, '');
		options = { ...options };
		delete options.approxPos;
	}

	const store = Store.get();
	let showtimesPage = Object.values(store.appData.pages).find(page => page.type === 'showtimes' || page.type === 'filmPage') || {};
	if (showtimesPage?.type === 'filmPage') {
		showtimesPage = Object.values(showtimesPage.data).find(page => page.type === 'showtimes') || {};
	}

	const apiUsageInterstetialEnabled = accessNested(store, 'appData.options.apiUsageInterstetial', false);
	const spreadsheetId = accessNested(showtimesPage, 'data.spreadsheetId', null);
	const useV2 = accessNested(showtimesPage, 'data.useV2', false);


	// Create required rules
	const rules = accessNested(store, 'appData.pages.boltShowtimes.data.rules', {});
	if (rules.changesSheet?.id && rules.changesSheet?.name) {
		options.rules_groups = [`manualEntry::${rules.changesSheet?.id}::${rules.changesSheet?.name}`]
			.concat(options.rules_groups || []);
	}
	if (rules.customRule?.name) {
		const parameters = rules.customRule.parameters;
		const normParameters = parameters
			? `::${parameters.replace(/\s*,\s*/ig, '::')}`
			: '';
		options.rules_groups = [`${rules.customRule.name}${normParameters}`]
			.concat(options.rules_groups || []);
	}
	if (Object.keys(rules).length >= (rules.customRule ? 1 : 0)) {
		let id = accessNested(store, 'appData.meta.id');
		let page = Store.get().activePage;
		options.rules_groups = [`appId::${id}::${accessNested(store, `appData.pages.${page}.path`)}`].concat(options.rules_groups || []);
	}

	// backward compatibility for builds without API keys
	let host = 'https://showtimes.s-prod.pow.io/v1.1';
	if (store.apiKey && apiUsageInterstetialEnabled) {
		fetchOptions.headers['X-API-Key'] = store.apiKey;
		host = 'https://showtimes.pow.io/v1.1';
	}
	if (useV2) {
		if (window.location.hostname === 'localhost') {
			fetchOptions.headers['pow-cache-include'] = 'theatersCore';
		}
		host = 'https://showtimes-v2.s-prod.pow.io/v2.0';
	}


	if (spreadsheetId) {
		const isAltered = ['/filters', '/location', '/screenings/more', 'chain/more'].find(x => endpoint.indexOf(x) !== -1);
		endpoint = endpoint.replace(/dummy\//, '');
		if (isAltered) {
			delete fetchOptions.headers['X-API-Key'];
			host = `https://geolocated-results.s-prod.pow.io/v1.0/${spreadsheetId}`;
		}
	}

	if ((__IS_LOCAL__ || __IS_DEMO__) && /[?&]localserver([=&]|$)/i.test(location.search)) {
		host = host.endsWith('v1.1') ? 'http://localhost:3000/v1.1' : 'http://localhost:3300/v2.0';
	}
	// host = 'http://localhost:3000/v1.1';
	// host = 'http://localhost:3300/v2.0';
	// ^ to test with your local server

	if (store.limitExceeded) {
		Store.emit('LIMIT_EXCEEDED', true);
		throw 429;
	}

	let minimum = new Promise((resolve) => setTimeout(resolve, 250));
	let [res] = await Promise.all([
		fetch(joinPaths(`${host}`, endpoint) + toQueryString(options), fetchOptions),
		minimum
	]);

	if (!res.ok) {
		if (res.status === 429 && !store.usingLimitlessKey) {
			let limitlessKeyPreviouslyUsed = false;
			try {
				limitlessKeyPreviouslyUsed = localStorage.getItem('limitlessKey');
			} catch (e) {}
			if (limitlessKeyPreviouslyUsed) {
				Store.emit('UPDATE_API_KEY');
			} else {
				Store.get().set({ limitExceeded: true });
				Store.emit('LIMIT_EXCEEDED', true);
			}
		}
		throw res.status || res.error || res;
	}

	return res.json();
}

export async function homeEntApi(dataToSend) {
	if (typeof window === 'undefined') {
		throw new Error('Trying to request server-side');
	}

	const { justWatchId, jwRegion, jwFilmIdType, fafId, thundrData, useDummyData } = dataToSend;

	const baseUrl = 'https://homeent.s-prod.pow.io/bolt-home-ent';
	const query = {};

	if (justWatchId && jwRegion) {
		query.jwFilmId = justWatchId;
		query.jwLocale = jwSupportedLocales[jwRegion.toLowerCase()];
	}

	if (jwRegion && useDummyData) {
		query.jwLocale = jwSupportedLocales[jwRegion.toLowerCase()];
	}

	if (jwFilmIdType) {
		query.jwFilmIdType = jwFilmIdType;
	}

	if (fafId) {
		query.fafFilmId = fafId;
	}

	if (useDummyData) {
		query.useDummyData = 'true';
	}

	const res = await fetch(baseUrl + toQueryString(query), {
		method: 'post',
		headers: {
			'content-type': 'application/json'
		},
		body: JSON.stringify(thundrData)
	});
	if (!res.ok) {
		throw res.status || res.error || res;
	}

	return res.json();
}

// We should probably have this mapping on the server-side instead to avoid bloating the app's JS with it
const jwSupportedLocales = {
	us: 'en_US', //united states
	gb: 'en_GB', //united kingdom
	fr: 'fr_FR', //france
	de: 'de_DE', //germany
	br: 'pt_BR', //brazil
	au:'en_AU', //Australia
	nz:'en_NZ', //New Zealand
	ca:'en_CA', //Canada for both ca or ca_fr
	ca_fr:'en_CA', //Canada for both ca or ca_fr
	za:'en_ZA', //South Africa
	ie:'en_IE', //Ireland
	mx:'es_MX', //Mexico
	jp:'ja_JP', //Japan
	nl:'en_NL', //Netherlands
	be:'fr_BE', //Belgium for be, be_fr and be_nl
	be_fr:'fr_BE', //Belgium for be, be_fr and be_nl
	be_nl:'fr_BE', //Belgium for be, be_fr and be_nl
	se:'sv_SE', //Sweden
	th:'en_TH', //Thailand
	pt:'pt_PT', //Portugal
	cz:'cs_CZ', //Czech Republic
	no:'en_NO', //Norway
	ru:'ru_RU', //Russia
	in:'en_IN', //India
	ch:'de_CH', //Switzerland for ch, ch_ch and ch_fr
	ch_ch:'de_CH', //Switzerland for ch, ch_ch and ch_fr
	ch_fr:'de_CH', //Switzerland for ch, ch_ch and ch_fr
	at:'de_AT', //Austria
	ph:'en_PH', //Philippines
	sg:'en_SG', //Singapore
	pl:'pl_PL', //Poland
	fi:'fi_FI', //Finland
	gr:'el_GR', //Greece
	tr:'tr_TR', //Turkey
	co:'es_CO', //Colombia
	dk:'en_DK', //Denmark
	ar:'es_AR', //Argentina
	cl:'es_CL', //Chile
	es:'es_ES', //Spain
	kr:'ko_KR', //South Korea
	it:'it_IT', //Italy
	lt:'en_LT', //Lithuania
	pe:'es_PE', //Peru
	ee:'en_EE', //Estonia
	lv:'en_LV', //Latvia
	my:'en_MY', //Malaysia
	id:'en_ID', //Indonesia
	hu:'hu_HU', //Hungary
	ve:'es_VE', //Venezuela
	ro:'ro_RO', //Romania
	ec:'es_EC' //Ecuador
};

export async function imageShareApi(endpoint, { imageUrl, siteUrl, metaOverrides }) {
	if (typeof window === 'undefined') {
		throw new Error('Trying to request server-side');
	}

	const baseUrl = 'https://share.s-prod.pow.io';
	const query = {
		imageUrl,
		siteUrl,
		metaOverrides
	};

	const res = await fetch(joinPaths(baseUrl, endpoint) + toQueryString(query));
	if (!res.ok) {
		throw res.status || res.error || res;
	}

	return res.text();
}
